import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import DynamicForm from 'components/Shared/DynamicForm'
import MaterialSchema from 'schemas/materials.schema.json'
import { useAppContext } from 'hooks/useAppContext'
import { addMaterial, applyMaterialDefaults, updateMaterial } from 'utils/FieldMaterialsUtils'
import { getFieldForm } from 'utils/ProjectConfigurationUtil'
import { useFormContext } from 'hooks/useFormContext'
import SkeletonForm from 'components/Shared/SkeletonForm'
import ODataUtil from 'utils/ODataUtil'
import { Button } from '@material-ui/core'
import AssignFieldForms from 'components/Materials/AssignFieldForms'
import { FormProvider as DynamicFormProvider } from 'context/FormContext'
import FormProvider from './FormProvider'
import MaterialDynamicForm from './MaterialDynamicForm'
import AuthCheck from 'components/Shared/AuthCheck'
import FieldMaterials from 'classes/FieldMaterials'


const AddMaterials = ({ orientation, defaultValues, CRUDType, callback, disabledProperties, hiddenProperties }) => {

    const _disabledProperties = disabledProperties;
    const _hiddenProperties = hiddenProperties;

    const _defaultValues = React.useMemo(() => ODataUtil.unCoupleExpandsFromDefaults(defaultValues), [defaultValues]);
    !disabledProperties.includes("FieldForms") && disabledProperties.push("FieldForms");

    const { selectedProject, selectedProjectData } = useAppContext()
    const [st, setSt] = useState({ open: false, uploadDialog: false, formName: [], formsList: [], formValues: _defaultValues })
    const { ready, schema, updateSchema, definitions } = useFormContext();
    const updateState = (updatedValues) => setSt(prevState => { return { ...prevState, ...updatedValues } })
    const setTestProcedures = () => updateState({ open: true })
    const currentFieldForms = _defaultValues.FieldForms !== undefined && _defaultValues.FieldForms.length > 0 ? _defaultValues.FieldForms.split(',') : []


    const handleClose = () => {
        updateState({ open: false, uploadDialog: false, uploadError: null })
    }

    const addFormsToMaterial = (values, formList) => {
        const formName = values.split(',');
        st.formValues.FieldFormId = formList.filter((form) => formName.includes(form.TestType)).map(value => value.FormId)
        st.formValues.FieldForms = values
        updateState({ formValues: st.formValues })
        handleClose()
    }

    /* const handleDelete = (chipToDelete) => () => {
         const removeForm = st.formName.filter((chip) => chip !== chipToDelete)
         updateState({ formName: removeForm })
     }*/

    useEffect(() => {
        !schema && updateSchema(MaterialSchema);
    }, [schema, updateSchema]);

    /** 
     * once schema is ready, get field form list so that it can be used in updates.
    */
    useEffect(() => {
        const asyncEffect = () => {
            getFieldForm({ projectId: selectedProject, isActive: true }).then(r => {
                updateState({ formsList: r.data })
            })
        }
        ready && asyncEffect();
    }, [ready, selectedProject]);

    if (CRUDType === "INSERT") {
        !_disabledProperties.includes('IsActive') && _disabledProperties.push('IsActive') && _hiddenProperties.push('IsActive');
    } else {
        _disabledProperties.includes('IsActive') && _disabledProperties.splice(_disabledProperties.indexOf('IsActive'), 1);
        _hiddenProperties.includes('IsActive') && _hiddenProperties.splice(_hiddenProperties.indexOf('IsActive'), 1);
    }

    const onSubmit = async (values) => {
        let result
        if (CRUDType === "INSERT") {
            if (st.formValues.FieldFormId !== null && !Array.isArray(st.formValues.FieldFormId)) {
                addFormsToMaterial(values.FieldForms, st.formsList)
            }
            st.formValues.FieldFormId === null || st.formValues.FieldFormId === "" ? values.FieldFormId = [] : values.FieldFormId = st.formValues.FieldFormId
            result = await addMaterial(selectedProject, values)
            if (result.success) {
                setTimeout(callback, 1000)
            } else {
                // form will still be available for submittal
            };
        } else if (CRUDType === "UPDATE") {
            if (st.formValues.FieldFormId !== null && !Array.isArray(st.formValues.FieldFormId)) {
                addFormsToMaterial(values.FieldForms, st.formsList)
            }
            st.formValues.FieldFormId === null || st.formValues.FieldFormId === "" ? values.FieldFormId = [] : values.FieldFormId = st.formValues.FieldFormId
            result = await updateMaterial(selectedProjectData, values, st.formValues)
            if (result.success) {
                setTimeout(callback, 1000);
            } else {
                // form will still be available for submittal
            };
        } else if (CRUDType === "UPDATE_LOCAL") {
            values.fieldMaterialId = st.formValues.FieldMaterialId;
            if (st.formValues.FieldFormId !== null && !Array.isArray(st.formValues.FieldFormId)) {
                addFormsToMaterial(values.FieldForms, st.formsList)
            };
            if(Array.isArray(st.formValues.FieldFormId)) { values.FieldFormId = st.formValues.FieldFormId; };
            const atts = applyMaterialDefaults(values, st.formValues);
            let _material =  new FieldMaterials(atts)
            result = await new Promise(res => res({success: true}));
            setTimeout(() => callback(_material), 1000);
        }
        return result
    }

    return (
        <AuthCheck scope="Materials" action="create" showWarning={true}>
            <div>
                <FormProvider.Provider value={st.open}>
                    <AssignFieldForms onClick={handleClose} updateForms={addFormsToMaterial} currentvalues={currentFieldForms} />

                </FormProvider.Provider>
                <div style={{ paddingLeft: '25px', paddingTop: '15px' }}><Button size="small" color="primary" onClick={setTestProcedures}>Assign Field Forms</Button></div>
                <div>
                    {
                        selectedProjectData && ready ?
                            <MaterialDynamicForm DynamicForm={DynamicForm} schema={schema} orientation={orientation} onSubmit={onSubmit} defaultValues={st.formValues} disabledProperties={_disabledProperties} hiddenProperties={_hiddenProperties} definitions={definitions} />
                            :
                            <SkeletonForm />
                    }
                </div>
            </div>
        </AuthCheck>
    )
}

AddMaterials.propTypes = {
    orientation: PropTypes.string,
    defaultValues: PropTypes.object,
    CRUDType: PropTypes.oneOf(['INSERT', 'UPDATE', 'UPDATE_LOCAL']).isRequired,
    callback: PropTypes.func,
    disabledProperties: PropTypes.array,
    hiddenProperties: PropTypes.array
}

AddMaterials.defaultProps = {
    orientation: "Vertical",
    callback: () => { },
    defaultValues: {},
    disabledProperties: [],
    hiddenProperties: []
}
// wrap form in its own FormProvider to make sure we don't have any conflicts with other forms
const Wrapped = (props) => <DynamicFormProvider><AddMaterials {...props} /></DynamicFormProvider>

export default Wrapped
